import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard, PlusCircle, ReceiptText, Home, Wallet, BarChart3,
  CheckSquare, ScrollText, Users, Settings, LogOut, Menu, X,
} from "lucide-react";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, roles: ["admin", "manager", "staff"], end: true },
  { to: "/add", label: "Add Expense", icon: PlusCircle, roles: ["admin", "manager", "staff"] },
  { to: "/expenses", label: "Expenses", icon: ReceiptText, roles: ["admin", "manager", "staff"] },
  { to: "/sites", label: "Homes", icon: Home, roles: ["admin", "manager", "staff"] },
  { to: "/budgets", label: "Budgets", icon: Wallet, roles: ["admin", "manager"] },
  { to: "/reports", label: "Reports", icon: BarChart3, roles: ["admin", "manager"] },
  { to: "/reconcile", label: "Reconciliation", icon: CheckSquare, roles: ["admin", "manager"] },
  { to: "/audit", label: "Audit Trail", icon: ScrollText, roles: ["admin", "manager"] },
  { to: "/users", label: "Users", icon: Users, roles: ["admin"] },
  { to: "/settings", label: "Settings", icon: Settings, roles: ["admin"] },
];

const MOBILE = [
  { to: "/", label: "Home", icon: LayoutDashboard, end: true },
  { to: "/expenses", label: "Expenses", icon: ReceiptText },
  { to: "/add", label: "Add", icon: PlusCircle, primary: true },
  { to: "/sites", label: "Homes", icon: Home },
];

const roleLabel = { admin: "System Administrator", manager: "Finance Manager", staff: "Staff" };

export default function Layout({ children }) {
  const { user, settings, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const items = NAV.filter((n) => n.roles.includes(user.role));

  const doLogout = async () => { await logout(); nav("/login"); };

  const SidebarInner = () => (
    <div className="flex flex-col h-full">
      <div className="px-6 py-6 border-b border-white/10">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-xl bg-white/10 ring-1 ring-white/15 flex items-center justify-center">
            <Wallet className="h-5 w-5 text-white" />
          </div>
          <div className="leading-none">
            <div className="flex items-center gap-1.5">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
              <span className="font-head font-bold text-white text-[13px] tracking-[0.28em]">AYOBELL</span>
            </div>
            <div className="font-head font-semibold text-white text-lg leading-none mt-1.5" data-testid="brand-name">{settings.product_name}</div>
            <div className="text-[10px] text-slate-400 mt-1 tracking-wide">Smart spending. Complete visibility.</div>
          </div>
        </div>
      </div>
      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto hide-scrollbar">
        {items.map((n) => (
          <NavLink
            key={n.to} to={n.to} end={n.end} onClick={() => setOpen(false)}
            data-testid={`nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors duration-200 ${
                isActive ? "bg-white/10 text-white" : "text-slate-400 hover:text-white hover:bg-white/5"
              }`
            }
          >
            <n.icon className="h-[18px] w-[18px]" />
            {n.label}
          </NavLink>
        ))}
      </nav>
      <div className="px-3 py-4 border-t border-white/10">
        <div className="px-3 py-2">
          <div className="text-sm font-medium text-white truncate" data-testid="sidebar-user-name">{user.name}</div>
          <div className="text-[11px] text-slate-400">{roleLabel[user.role]}</div>
        </div>
        <button data-testid="logout-btn" onClick={doLogout}
          className="mt-1 w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-slate-400 hover:text-white hover:bg-white/5 transition-colors duration-200">
          <LogOut className="h-[18px] w-[18px]" /> Sign out
        </button>
        <div className="px-3 pt-3 mt-2 border-t border-white/10 text-[10px] leading-tight text-slate-500" data-testid="sidebar-powered-by">
          Powered by <span className="text-slate-300 font-semibold tracking-wide">AYOBELL</span>
          <div className="mt-0.5">TechSpace Solutions Ltd</div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex flex-col fixed inset-y-0 left-0 w-[280px] bg-[#0F172A] z-30">
        <SidebarInner />
      </aside>

      {/* Mobile drawer */}
      {open && (
        <div className="lg:hidden fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/50" onClick={() => setOpen(false)} />
          <div className="absolute inset-y-0 left-0 w-[280px] bg-[#0F172A]">
            <button className="absolute top-4 right-4 text-slate-400" onClick={() => setOpen(false)}><X className="h-5 w-5" /></button>
            <SidebarInner />
          </div>
        </div>
      )}

      <div className="lg:pl-[280px]">
        {/* Top header */}
        <header className="sticky top-0 z-20 h-16 bg-white border-b border-border flex items-center justify-between px-4 lg:px-8">
          <div className="flex items-center gap-3">
            <button className="lg:hidden text-slate-700" data-testid="mobile-menu-btn" onClick={() => setOpen(true)}><Menu className="h-6 w-6" /></button>
            <div className="lg:hidden font-head font-semibold text-slate-900"><span className="tracking-[0.15em]">AYOBELL</span> {settings.product_name}</div>
          </div>
          <div className="hidden lg:flex items-center gap-2 text-sm text-slate-500">
            Signed in as <span className="font-medium text-slate-800">{user.name}</span>
            <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 text-xs">{roleLabel[user.role]}</span>
          </div>
        </header>

        <main className="p-4 lg:p-8 pb-24 lg:pb-8 max-w-[1400px]">{children}</main>
      </div>

      {/* Mobile bottom nav */}
      <nav className="lg:hidden fixed bottom-0 inset-x-0 z-30 bg-white border-t border-border h-16 grid grid-cols-4">
        {MOBILE.map((n) => (
          <NavLink key={n.to} to={n.to} end={n.end}
            data-testid={`mobnav-${n.label.toLowerCase()}`}
            className={({ isActive }) =>
              `flex flex-col items-center justify-center gap-0.5 text-[11px] ${
                n.primary ? "text-white" : isActive ? "text-slate-900 font-semibold" : "text-slate-400"
              }`
            }>
            {n.primary ? (
              <div className="h-11 w-11 -mt-6 rounded-full bg-[#0F172A] flex items-center justify-center shadow-lg">
                <n.icon className="h-6 w-6 text-white" />
              </div>
            ) : (
              <n.icon className="h-5 w-5" />
            )}
            <span className={n.primary ? "text-slate-700 mt-0.5" : ""}>{n.label}</span>
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
